import type { DashboardKpis, DashboardPayload, DashboardRange, Order, ProductSummary } from "../../api/types";

export const RANGE_DAYS: Record<DashboardRange, number> = { "7d": 7, "30d": 30, "90d": 90 };
export const DAY_MS = 86_400_000;

function isRevenueOrder(order: Order): boolean {
  return order.status === "completed";
}

function inWindow(order: Order, start: number, end: number): boolean {
  const t = Date.parse(order.createdAt);
  return t >= start && t < end;
}

function deltaPct(current: number, previous: number): number {
  if (previous === 0) return current === 0 ? 0 : 100;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

function revenueOf(orders: Order[]): number {
  return orders.filter(isRevenueOrder).reduce((sum, o) => sum + o.amount, 0);
}

/**
 * Aggregates a store's orders into the dashboard payload for one range.
 * `now` is the anchor instant ("today") — the handler passes the freshest
 * order timestamp rather than the wall clock so the dashboard stays
 * populated no matter when the prototype is opened relative to the seed's
 * fixed reference date.
 */
export function aggregateDashboard(orders: Order[], range: DashboardRange, now: number): DashboardPayload {
  const days = RANGE_DAYS[range];
  const periodEnd = now;
  const periodStart = periodEnd - days * DAY_MS;
  const previousStart = periodStart - days * DAY_MS;

  const current = orders.filter((o) => inWindow(o, periodStart, periodEnd));
  const previous = orders.filter((o) => inWindow(o, previousStart, periodStart));

  const revenue = revenueOf(current);
  const previousRevenue = revenueOf(previous);
  const ordersCount = current.length;
  const previousOrdersCount = previous.length;
  const customers = new Set(current.map((o) => o.buyer.id)).size;
  const previousCustomers = new Set(previous.map((o) => o.buyer.id)).size;

  // No separate traffic model exists in this mock, so conversion rate is
  // derived from order volume against a stable, believable session multiple.
  const conversionRate = (count: number) => (count === 0 ? 0 : Math.round((count / (count * 12)) * 10000) / 100);

  const bucketed = (count: number, metric: (bucket: Order[]) => number): number[] => {
    const bucketMs = (days * DAY_MS) / count;
    return Array.from({ length: count }, (_, i) => {
      const bucketStart = periodStart + i * bucketMs;
      const bucketEnd = bucketStart + bucketMs;
      return metric(current.filter((o) => inWindow(o, bucketStart, bucketEnd)));
    });
  };

  const kpis: DashboardKpis = {
    revenue: {
      label: "Revenue",
      value: revenue,
      deltaPct: deltaPct(revenue, previousRevenue),
      sparkline: bucketed(7, revenueOf),
    },
    orders: {
      label: "Orders",
      value: ordersCount,
      deltaPct: deltaPct(ordersCount, previousOrdersCount),
      sparkline: bucketed(7, (bucket) => bucket.length),
    },
    conversionRate: {
      label: "Conversion rate",
      value: conversionRate(ordersCount),
      deltaPct: deltaPct(conversionRate(ordersCount), conversionRate(previousOrdersCount)),
      sparkline: bucketed(7, (bucket) => conversionRate(bucket.length)),
    },
    customers: {
      label: "Customers",
      value: customers,
      deltaPct: deltaPct(customers, previousCustomers),
      sparkline: bucketed(7, (bucket) => new Set(bucket.map((o) => o.buyer.id)).size),
    },
  };

  const revenueSeries = Array.from({ length: days }, (_, i) => {
    const daysAgo = days - 1 - i;
    const dayStart = periodEnd - (daysAgo + 1) * DAY_MS;
    const dayEnd = periodEnd - daysAgo * DAY_MS;
    const dayOrders = current.filter((o) => inWindow(o, dayStart, dayEnd));
    return {
      date: new Date(dayEnd).toISOString().slice(0, 10),
      revenue: revenueOf(dayOrders),
      orders: dayOrders.length,
    };
  });

  const byProduct = new Map<string, { product: ProductSummary; revenue: number; unitsSold: number }>();
  for (const order of current.filter(isRevenueOrder)) {
    const entry = byProduct.get(order.product.id) ?? { product: order.product, revenue: 0, unitsSold: 0 };
    entry.revenue += order.amount;
    entry.unitsSold += 1;
    byProduct.set(order.product.id, entry);
  }
  const topProducts = [...byProduct.values()]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)
    .map((entry) => ({ ...entry.product, revenue: entry.revenue, unitsSold: entry.unitsSold }));

  const recentOrders = [...current]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, 10);

  return { range, kpis, revenueSeries, topProducts, recentOrders };
}
